import { Sparkles, Award, Users, ArrowRight, Zap, Shield, Globe } from "lucide-react";
import TextType from "./TextType";
import FloatingLines from "./FloatingLines";
import { useNavigate } from "react-router-dom";

const stats = [
  { value: "850+", label: "Projects Delivered", icon: Users },
  { value: "99%", label: "Client Satisfaction", icon: Award },
  { value: "5.0", label: "Average Rating", icon: Sparkles },
];

const features = [
  { icon: Globe, text: "Global Reach" },
  { icon: Zap, text: "Fast Delivery" },
  { icon: Shield, text: "Secure Solutions" },
];

export const Hero = () => {
  const navigate = useNavigate(); 

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="absolute inset-0">
        <FloatingLines
          enabledWaves={['top', 'middle', 'bottom']}
          lineCount={[10, 15, 20]}
          lineDistance={[8, 6, 4]}
          interactive={true}
          parallax={true}
        />
      </div>

      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-600/20 via-purple-600/15 to-transparent rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-transparent rounded-full blur-3xl animate-pulse-soft" />

      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center min-h-screen py-20 text-center">

          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-8 animate-fade-in"
            style={{
              background: 'rgba(108, 60, 255, 0.10)',
              borderColor: 'rgba(120, 80, 255, 0.35)',
            }}
          >
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: '#9B6FFF', boxShadow: '0 0 6px #9B6FFF' }}
            />
            <span
              className="text-xs font-medium tracking-widest uppercase"
              style={{ color: '#B89EFF', letterSpacing: '0.12em' }}
            >
              Muhali Software Solutions
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight mb-6 animate-slide-up">
            <span className="text-white">Transform Your Vision into</span>
            <br />
            <span className="inline-block mt-2">
              <TextType
                text={[
                  "Stunning Websites",
                  "Digital Experiences",
                  "Custom Solutions",
                  "Powerful Strategies",
                ]}
                typingSpeed={60}
                pauseDuration={2500}
                showCursor={true}
                cursorCharacter="|"
                renderText={(text) => (
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    {text}
                  </span>
                )}
              />
            </span>
          </h1>

          <p className="max-w-3xl text-lg sm:text-xl text-slate-300 leading-relaxed mb-4 animate-slide-up">
            <span className="font-semibold text-blue-400">Grow Your Business</span> with{" "}
            <span className="font-semibold text-purple-400">Affordable Web Design</span> and{" "}
            <span className="font-semibold text-pink-400">Digital Marketing</span>.
          </p>

          <p className="max-w-3xl text-base sm:text-lg text-slate-400 leading-relaxed mb-10 animate-slide-up">
            We develop stunning websites and powerful digital strategies that drive real results.
            Partner with experts who turn your business goals into measurable success.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-slide-up">
            <button
              onClick={() => navigate("/Contact")}
              className="text-base sm:text-lg px-10 py-4 font-bold tracking-wide rounded-xl shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white hover:scale-105 group flex items-center justify-center"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-16 animate-fade-in">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300"
              >
                <feature.icon className="w-4 h-4 text-purple-400" />
                {feature.text}
              </div>
            ))}
          </div>

          <div className="w-full max-w-4xl mx-auto animate-fade-in">
           <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
  {stats.map((stat, index) => (
    <div key={index} className="group cursor-pointer">
      <div
        className="relative p-6 rounded-2xl transition-all duration-300 hover:scale-105"
        style={{
          background: 'rgba(108, 60, 255, 0.06)',
          border: '1px solid rgba(120, 80, 255, 0.18)',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(155, 111, 255, 0.45)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(120, 80, 255, 0.18)'}
      >
        {/* Top row: label + icon */}
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-medium uppercase tracking-widest"
            style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em' }}
          >
            {stat.label}
          </span>
          <stat.icon
            className="w-4 h-4 transition-colors duration-300"
            style={{ color: 'rgba(155, 111, 255, 0.4)' }}
          />
        </div>

        {/* Stat value */}
        <div
          className="text-4xl sm:text-5xl font-bold tracking-tight"
          style={{ color: '#fff', fontFamily: "'Syne', sans-serif" }}
        >
          {stat.value}
        </div>

        {/* Bottom accent line */}
        <div
          className="mt-4 h-px w-8 transition-all duration-300 group-hover:w-16"
          style={{ background: '#9B6FFF' }}
        />
      </div>
    </div>
  ))}
</div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
            <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
              <div className="w-1 h-2 rounded-full bg-white/40 animate-pulse" />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};